import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Vehicle } from 'rest-client';

import { GoogleTagManagerService } from '@app/modules/core/services/analytics/google-tag-manager.service';
import { OfferService } from '@app/modules/core/services/offer/offer.service';
import { VehicleService } from '@app/modules/core/services/vehicle/vehicle.service';

import { VehicleReturned } from '../../models';

/**
 * > Selects Vehicle from Client List
 */
@UntilDestroy()
@Component({
  selector: 'bkg-vehicle-dialog-select-client-car',
  templateUrl: './vehicle-dialog-select-client-car.component.html',
})
export class VehicleDialogSelectClientCarComponent
  implements OnInit, OnChanges {
  @Output() selectedVehicleReturn = new EventEmitter<VehicleReturned>();
  @Output() errorReturn = new EventEmitter<HttpErrorResponse>();
  @Output() addNewVehicleViewReturn = new EventEmitter<boolean>();

  @Input() addMoreInfoClient: boolean;
  @Input() clientId: number;

  vehicles: Vehicle[];
  clientCarForm: FormGroup;
  loadingVehicles: boolean;

  constructor(
    private vehicleService: VehicleService,
    private formBuilder: FormBuilder,
    private googleTagManagerService: GoogleTagManagerService,
    private offerService: OfferService
  ) {}

  ngOnInit(): void {
    this.getClientVehicles();
    this.buildClientCarForm();
  }

  ngOnChanges(): void {
    if (this.addMoreInfoClient) {
      this.getControl('id').disable();
    }
  }

  private buildClientCarForm = (): void => {
    this.clientCarForm = this.formBuilder.group({
      id: [null, [Validators.required]],
    });
  };

  private getControl(controlName: string): AbstractControl {
    return this.clientCarForm.get(controlName);
  }

  private getClientVehicles = (): void => {
    this.loadingVehicles = true;
    this.vehicleService
      .listClientVehicles()
      .pipe(untilDestroyed(this))
      .subscribe(
        (vehicles: Vehicle[]) => {
          this.vehicles = vehicles;
          this.loadingVehicles = false;
        },
        (error: HttpErrorResponse) => {
          this.errorReturn.emit(error);
          this.loadingVehicles = false;
        }
      );
  };

  selectVehicle = (): void => {
    const vehicleId = this.clientCarForm.get('id').value as number;

    this.vehicleService
      .getClientVehicle(vehicleId, this.clientId)
      .pipe(untilDestroyed(this))
      .subscribe(
        (vehicle: Vehicle) => {
          this.googleTagManagerService.pushOnDataLayer({
            event: 'vehiculo-seleccionado-dropdown',
            'vehiculo-marca': vehicle?.vehicle_information?.make_description,
            'vehiculo-modelo': vehicle?.vehicle_information?.model_description,
          });
          this.googleTagManagerService.pushBookingActionOnDataLayer({
            event_name: 'vehiculo-seleccionado-dropdown',
            'vehiculo-marca': vehicle?.vehicle_information?.make_description,
            'vehiculo-modelo': vehicle?.vehicle_information?.model_description,
          });
          if (!vehicle.id_gt_vehicle_information && vehicle.plate) {
            this.checkPlateClientVehicle(vehicle);
          } else {
            this.selectedVehicleReturn.emit({
              vehicle: vehicle,
              type: 'clientVehicle',
            });
          }
        },
        (error: HttpErrorResponse) => this.errorReturn.emit(error)
      );
  };

  addNewVehicle = (): void => this.addNewVehicleViewReturn.emit(true);

  private checkPlateClientVehicle = (selectedVehicle: Vehicle): void => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const payload = { plate: selectedVehicle.plate } as any;
    if (this.clientId) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      payload.client_id = this.clientId;
    }
    this.vehicleService
      .checkClientVehiclePlate(payload)
      .pipe(untilDestroyed(this))
      .subscribe(
        (vehicle: Vehicle) => {
          if (!vehicle.id_gt_vehicle_information) {
            this.selectedVehicleReturn.emit({
              vehicle: selectedVehicle,
              type: 'clientVehicle',
            });
          } else {
            vehicle.id = selectedVehicle.id;
            if (this.clientId) {
              this.selectedVehicleReturn.emit({
                vehicle: vehicle,
                type: 'clientVehicle',
              });
            } else {
              this.vehicleService
                .updateUserVehicle(vehicle)
                .pipe(untilDestroyed(this))
                .subscribe(
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  (userVehicle: Vehicle) => {
                    this.selectedVehicleReturn.emit({
                      vehicle: vehicle,
                      type: 'clientVehicle',
                    });
                  },
                  (error: HttpErrorResponse) => this.errorReturn.emit(error)
                );
            }
          }
        },
        (error: HttpErrorResponse) => this.errorReturn.emit(error)
      );
  };
}
